import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiLink } from 'react-icons/fi';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import CountdownTimer from '../components/CountdownTimer/CountdownTimer';
import Breadcrumbs from '../components/breadcrumbs/Breadcrumbs';
import { Navbar } from '../components';
import {
  generateOrganizationSchema,
  generateCourseSchema,
  generateLocalBusinessSchema,
  generateFAQSchema,
} from '../utils/seoSchema';
import './CoursesPage.css';

/* ─────────────── Course data ─────────────── */
const COURSES = [
  {
    id: 'fullstack',
    flagship: true,
    title: 'JavaScript Fullstack Development',
    subtitle: 'React.js · Next.js · Node.js · MongoDB · REST APIs',
    duration: '3 Months',
    price: '₹20,000',
    gst: '+ GST',
    badges: ['🏆 Flagship', '🎯 Placement Support', '🤖 AI Assisted'],
    badgeTypes: ['flagship-badge', 'placement-badge', 'ai-badge'],
    image: 'https://images.unsplash.com/photo-1627398242454-45a1465c2479?w=900&auto=format&fit=crop&q=80',
    imageAlt: 'JavaScript Fullstack Development',
    highlights: [
      { icon: '💼', label: 'Industry Live Projects' },
      { icon: '🤖', label: 'AI Assisted Coding' },
      { icon: '🎯', label: 'Placement Support' },
      { icon: '🗣️', label: 'Mock Interviews' },
      { icon: '🔀', label: 'Git & GitHub Workflow' },
      { icon: '📄', label: 'ATS Resume Building' },
    ],
    description: 'Master the complete JavaScript ecosystem — from pixel-perfect UIs with React & Next.js to scalable APIs with Node.js. Build real-world, industry-grade projects with AI-assisted development, expert mentors from top IT companies, and dedicated placement support.',
    ctaLabel: 'Enroll Now',
    ctaVariant: 'primary',
    syllabus: [
      {
        module: 'HTML5 & CSS3',
        topics: [
          'HTML5 Semantic Markup & Structure',
          'Forms & Input Validation',
          'CSS3 Properties & Selectors',
          'Flexbox & CSS Grid',
          'Responsive Design & Media Queries',
        ],
      },
      {
        module: 'JavaScript Fundamentals',
        topics: [
          'var vs let vs const - Scoping & Hoisting',
          'Object-Oriented Programming (OOPs) - Classes, Inheritance, Polymorphism',
          'Encapsulation, Abstraction & Single Responsibility Principle',
          'Arrays - Methods, Iteration, Immutability, Performance',
          'Strings - Methods, Template Literals, String Manipulation',
          'Sets & Maps - Usage, Performance, WeakSet, WeakMap',
          'Closures & Lexical Scoping',
          'this Keyword - Binding, call/apply/bind',
          'Prototypes & Prototype Chain - Prototypal Inheritance',
          'Constructor Functions vs Classes',
          'Async JavaScript - Callbacks, Promises, async/await',
          'Event Loop, Call Stack, Task Queue, Microtasks',
          'ES6+ Features - Destructuring, Spread/Rest, Arrow Functions',
          'Higher-Order Functions & Composition',
          'Debounce vs Throttle & Memoization',
          'Generators & Iterators',
          'Symbols & Well-Known Symbols',
          'Proxy & Reflect - Metaprogramming',
          'Memory Management & Garbage Collection',
          'Design Patterns - Singleton, Observer, Factory, Module',
        ],
      },
      {
        module: 'React.js',
        topics: [
          'Virtual DOM & Reconciliation Algorithm',
          'Component Lifecycle - Class vs Hooks',
          'State Management - useState, useContext',
          'Props - Passing, Destructuring, PropTypes',
          'Controlled vs Uncontrolled Components',
          'Keys in Lists - Importance & Best Practices',
          'Hooks - useState, useEffect, useContext, useReducer',
          'useCallback & useMemo for Optimization',
          'useRef for DOM Access & Mutable Values',
          'Custom Hooks Creation & Patterns',
          'Hook Rules & Dependencies Array',
          'Higher-Order Components (HOC)',
          'Render Props Pattern',
          'Compound Components',
          'React.memo for Performance',
          'Code Splitting & Lazy Loading',
          'Error Boundaries',
          'Suspense for Async Components',
          'Memory Leaks & Cleanup Functions',
          'Infinite Loops in useEffect',
          'Stale Closures & Race Conditions',
          'Re-render Optimization Techniques',
          'Testing with React Testing Library',
        ],
      },
      {
        module: 'Next.js',
        topics: [
          'App Router - Pages & Layouts',
          'File-Based Routing System',
          'Server Components vs Client Components',
          'Server-Side Rendering (SSR)',
          'Static Site Generation (SSG)',
          'Incremental Static Regeneration (ISR)',
          'API Routes & Middleware',
          'Dynamic Routes & Catch-All Segments',
          'Image Optimization & next/image',
          'Font Optimization',
          'Code Splitting & Performance',
          'Data Fetching - fetch, cache, revalidate',
          'Environment Variables & Secrets',
          'Deployment Strategies',
          'Monitoring & Analytics',
        ],
      },
      {
        module: 'Node.js',
        topics: [
          'Modules - CommonJS vs ES6 Modules',
          'require vs import',
          'npm & package.json Management',
          'Node Event Loop & Non-blocking I/O',
          'Streams - Readable, Writable, Transform',
          'Buffers & Encoding',
          'File System Operations',
          'Process Management & Global Objects',
          'Child Processes & Worker Threads',
          'Environment Variables',
          'Error Handling & Stack Traces',
          'Debugging Node Applications',
          'Memory Management & Performance',
          'Clustering for Load Distribution',
          'Testing with Jest & Mocha',
        ],
      },
      {
        module: 'Express.js',
        topics: [
          'HTTP Module Basics & Request/Response',
          'Routing - Methods & Route Parameters',
          'Middleware - Order, Execution, Error Handling',
          'Request Body Parsing - JSON, Form-Data',
          'Response Status Codes & Headers',
          'Route Grouping & Organization',
          'Express Generators & Project Structure',
          'Error Handling Middleware',
          'CORS & Security Headers',
          'Authentication - JWT, Sessions, OAuth',
          'Input Validation & Sanitization',
          'File Uploads & Streaming',
          'Caching Strategies',
          'Rate Limiting & Throttling',
          'Logging & Monitoring',
          'Testing API Endpoints',
        ],
      },
      {
        module: 'Mongoose',
        topics: [
          'Schema Design, Validation & Data Types',
          'CRUD Operations - Create, Read, Update, Delete',
          'Query Methods & Filtering',
          'Population & References',
          'Aggregation Pipeline',
          'Indexes & Query Optimization',
          'N+1 Problem & Solutions',
          'Middleware, Hooks & Error Handling',
        ],
      },
      {
        module: 'GitHub Workflow',
        topics: [
          'Git Basics - init, add, commit, push, pull',
          'Repositories - Creating & Cloning',
          'Branches - Creating, Switching, Merging',
          'Pull Requests - Creation & Review Process',
          'Merge Conflicts & Resolution',
          'Branching Strategies - Git Flow, Trunk-Based',
          'Rebasing vs Merging',
          'Cherry-Pick & Reset Operations',
          'Tags & Releases',
          'GitHub Actions - CI/CD Automation',
          'Code Review Best Practices',
          'Commit Message Standards',
          'GitHub Issues & Project Management',
          'Collaboration Workflows',
        ],
      },
      {
        module: 'Git Copilot Integration',
        topics: [
          'GitHub Copilot Inline Completions',
          'Writing Effective Comments for Better Suggestions',
          'AI Code Suggestions & Auto Completion',
          'Smart Coding - Context Awareness',
          'Copilot Chat for Code Explanation',
          'Refactoring Code with AI Assistance',
          'Debugging Strategies with Copilot',
          'Generating Unit Tests with AI',
          'Documentation Generation',
          'Security Considerations with AI Code',
          'Performance Implications of Generated Code',
          'Code Review with AI Assistance',
          'Best Practices for Prompt Engineering',
          'Limitations & When Not to Use Copilot',
        ],
      },
      {
        module: 'REST API Development',
        topics: [
          'RESTful Architecture & HTTP Methods (GET, POST, PUT, DELETE, PATCH)',
          'Request & Response Formats, Status Codes',
          'API Design & Error Handling',
          'Pagination, Filtering & Sorting',
          'API Documentation - Swagger/OpenAPI',
          'API Testing with Postman',
          'Performance Optimization & Caching',
          'API Versioning & Rate Limiting',
        ],
      },
      {
        module: 'Authentication & Authorization',
        topics: [
          'JWT (JSON Web Tokens) - Structure, Claims & Verification',
          'Token Signing & Refresh Token Rotation',
          'Password Hashing with CryptoJS & Bcrypt',
          'Session & Cookie Security (HttpOnly, Secure, SameSite)',
          'Role-Based & Permission-Based Access Control',
        ],
      },
      {
        module: 'Figma Design to UI',
        topics: [
          'Understanding Figma UI & Design Tokens',
          'Converting Figma Designs to React Components',
          'Extracting Colors, Fonts, Spacing from Figma',
          'Component Mapping - Figma to Code',
          'Responsive Component Implementation',
          'Design System Integration & Consistency',
          'CSS-in-JS vs CSS Modules',
          'Atomic Design Principles',
          'Accessibility in UI Components',
          'Performance Considerations in Component Design',
        ],
      },
      {
        module: 'Deployment',
        topics: [
          'Deploy on Firebase - Hosting & Functions',
          'Deploy on Heroku - Buildpacks & Procfile',
          'Environment Variables & Secrets Management',
          'Production Best Practices',
          'Performance Optimization - Bundle Size & Code Splitting',
          'Image Optimization & Lazy Loading',
          'Logging in Production',
        ],
      },
      {
        module: 'Portfolio Projects',
        topics: [
          'Build 3 Production-Ready AI-Powered Web Applications',
          'Full-stack Implementation with React, Node.js, MongoDB',
          'Authentication, API Integration & Database Design',
          'Performance Optimization & Best Practices',
          'Deployment on Firebase/Heroku & Live on GitHub',
        ],
      },
      {
        module: 'Placement Support',
        topics: [
          'Presentation Skills & Technical Communication',
          'ATS-Friendly Resume Building',
          'Industry-Level Mock Interviews (Regular Basis)',
          'LinkedIn Optimization',
          'Expert Interaction Sessions from Top Leading IT Companies',
          'HR Interview Preparation & Soft Skills',
        ],
      },
    ],
  },
  {
    id: 'aws-devops',
    flagship: true,
    title: 'AWS Cloud with DevOps Essentials',
    subtitle: 'AWS Services · Cloud Computing · DevOps · Linux',
    duration: '2 Months',
    price: '₹15,000',
    gst: '+ GST',
    badges: ['🏆 Flagship', '☁️ Cloud Certified', '⚙️ DevOps Ready'],
    badgeTypes: ['flagship-badge', 'new-badge', 'placement-badge'],
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=900&auto=format&fit=crop&q=80',
    imageAlt: 'AWS Cloud & DevOps',
    highlights: [
      { icon: '☁️', label: 'AWS Core Services' },
      { icon: '🐧', label: 'Linux Fundamentals' },
      { icon: '🌐', label: 'Networking Basics' },
      { icon: '🐳', label: 'Docker & CI/CD' },
      { icon: '⚙️', label: 'DevOps Essentials' },
      { icon: '💼', label: 'Industry Projects' },
    ],
    description: 'Master cloud computing fundamentals, AWS services, Linux administration, and DevOps practices. Build deployment pipelines, containerize applications, and learn industry-standard tools for scalable infrastructure.',
    ctaLabel: 'Enroll Now',
    ctaVariant: 'primary',
    syllabus: [
      {
        module: '01 Cloud Computing Fundamentals',
        topics: [
          'Cloud Concepts',
          'AWS Global Infrastructure',
          'Regions & AZ',
          'Pricing Basics',
        ],
      },
      {
        module: '02 Linux Basics for AWS',
        topics: [
          'Linux Basics',
          'Users & Permissions',
          'Package Installation',
          'SSH Access',
        ],
      },
      {
        module: '03 Networking Basics',
        topics: [
          'IP Address',
          'DNS',
          'HTTP/HTTPS',
          'Ports',
          'Security Groups',
        ],
      },
      {
        module: '04 AWS Core Services',
        topics: [
          'EC2',
          'S3',
          'IAM',
          'VPC Basics',
          'Subnets',
        ],
      },
      {
        module: '05 Database Services',
        topics: [
          'RDS Overview',
          'MySQL on AWS',
          'Backups',
          'Connectivity',
        ],
      },
      {
        module: '06 Load Balancer & Auto Scaling',
        topics: [
          'Load Balancer',
          'Traffic Distribution',
          'Auto Scaling',
          'High Availability',
        ],
      },
      {
        module: '07 Deployment on AWS',
        topics: [
          'CICD Pipeline On AWS (IAM)',
          'Static Website (S3)',
          'Deploy Web Apps',
          'Nginx Basics',
          'Domain & DNS',
          'SSL / HTTPS',
        ],
      },
      {
        module: '08 DevOps Essentials',
        topics: [
          'DevOps Concepts',
          'Git & GitHub',
          'Docker Basics',
          'CI/CD Concepts',
        ],
      },
      {
        module: '09 Monitoring & Security',
        topics: [
          'CloudWatch Basics',
          'Logs & Alerts',
          'Security Best Practices',
          'IAM Security',
        ],
      },
      {
        module: '10 Industry Live Project',
        topics: [
          'Real-World Use Cases',
          'End-to-End Project',
          'Hands-on Deployment',
          'Best Practices',
        ],
      },
    ],
  },
  {
    id: 'frontend',
    flagship: false,
    title: 'Frontend Mastery',
    subtitle: 'HTML · CSS · JavaScript · React.js / Angular',
    duration: '2 Months',
    price: '₹15,000',
    gst: '+ GST',
    badges: ['✨ Beginner Friendly', '🎨 UI Focus'],
    badgeTypes: ['new-badge', 'placement-badge'],
    image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=900&auto=format&fit=crop&q=80',
    imageAlt: 'Frontend Web Development',
    highlights: [
      { icon: '🎨', label: 'UI/UX Fundamentals' },
      { icon: '⚡', label: 'Modern CSS' },
      { icon: '⚛️', label: 'React or Angular' },
      { icon: '📱', label: 'Responsive Design' },
      { icon: '🌐', label: 'Browser APIs' },
      { icon: '🎯', label: 'Project Portfolio' },
    ],
    description: 'Build stunning, responsive web interfaces from scratch. Start with solid HTML & CSS fundamentals, master JavaScript interactivity, and level up with a modern framework — React.js or Angular — chosen based on your career goals.',
    ctaLabel: 'Get Started',
    ctaVariant: 'secondary',
    syllabus: [
      {
        module: 'Module 1 — HTML5 Foundations',
        topics: [
          'HTML5 Document Structure & Semantic Tags (Header, Main, Article, Section, Nav)',
          'Forms, Input Types & Form Validation',
          'SEO-Friendly Markup & Meta Tags',
          'Accessibility (a11y) - ARIA Attributes & Screen Readers',
          'HTML5 APIs — Canvas, Video, Audio & Drag & Drop',
        ],
      },
      {
        module: 'Module 2 — Advanced CSS3',
        topics: [
          'CSS Box Model, Specificity & Cascade',
          'Flexbox — Complete Layout Mastery',
          'CSS Grid — Advanced 2D Layouts',
          'CSS Custom Properties (Variables)',
          'Animations, Transitions & Keyframes',
          'Responsive Design — Media Queries, Mobile First',
          'CSS Preprocessors — Sass/SCSS Basics',
        ],
      },
      {
        module: 'Module 3 — JavaScript for UI',
        topics: [
          'var vs let vs const - Scoping & Hoisting',
          'Object-Oriented Programming (OOPs) - Classes, Inheritance, Encapsulation',
          'Arrays - Methods, Iteration, Filtering, Mapping, Performance',
          'Strings - Methods, Template Literals, String Manipulation Techniques',
          'Sets & Maps - Use Cases, Performance, Weak Variants',
          'DOM Manipulation & Event Handling - Performance Optimization',
          'Event Delegation & Event Bubbling - Event Flow',
          'Browser APIs — Fetch, LocalStorage, History, IndexedDB',
          'Asynchronous JS — Promises & async/await, Error Handling',
          'Callbacks & Promise Chains - Common Patterns',
          'Closures & Lexical Scoping - Memory Implications',
          'this Keyword & Binding - call, apply, bind',
          'Prototypes & Prototype Chain - Prototypal Inheritance',
          'Higher-Order Functions & Function Composition',
          'Debounce & Throttle Techniques - Performance Optimization',
          'Debugging in Browser DevTools - Console, Debugger, Performance',
          'Memory Management & Performance - Profiling, Optimization',
        ],
      },
      {
        module: 'Module 4 — React.js / Angular',
        topics: [
          'Virtual DOM & Reconciliation Algorithm',
          'Component Lifecycle - Class vs Hooks',
          'State Management - useState, useContext',
          'Props - Passing, Destructuring, PropTypes',
          'Controlled vs Uncontrolled Components',
          'Keys in Lists - Importance & Best Practices',
          'Hooks - useState, useEffect, useContext, useReducer',
          'useCallback & useMemo for Optimization',
          'useRef for DOM Access & Mutable Values',
          'Custom Hooks Creation',
          'Hook Rules & Dependencies Array',
          'Higher-Order Components (HOC)',
          'Render Props Pattern',
          'React.memo for Performance',
          'Code Splitting & Lazy Loading',
          'Error Boundaries',
          'Memory Leaks & Cleanup Functions',
          'Re-render Optimization',
          'React Router v6 - Routes, Outlet, useNavigate',
          'API Integration & Data Fetching',
          'Form Handling & Validation',
          'Testing with React Testing Library',
        ],
      },
      {
        module: 'Module 5 — Project & Portfolio',
        topics: [
          'Project 1: AI-Powered Chatbot Application with NLP',
          'Project 2: ATS-Friendly Resume Scanner & Builder with AI',
          'Project 3: Intelligent Document Analyzer & Content Generator',
          'Git Version Control Workflow',
          'Performance Optimization & Lighthouse Audits',
          'Image Optimization & Lazy Loading',
          'Code Splitting & Bundle Analysis',
          'SEO Best Practices',
          'Accessibility (a11y) Implementation',
          'Mobile Responsive Design Testing',
          'Cross-Browser Compatibility',
          'Deployment on Firebase/Heroku',
          'Portfolio Presentation & GitHub Profile',
          'Documentation & README Files',
          'Performance Metrics & Monitoring',
        ],
      },
    ],
  },
  {
    id: 'ai-copilot',
    flagship: false,
    title: 'AI-Assisted Development with GitHub Copilot',
    subtitle: 'GitHub Copilot · Prompt Engineering · AI Code Review · OpenAI',
    duration: '1 Month',
    price: '₹12,000',
    gst: '+ GST',
    badges: ['🤖 AI Powered', '⚡ Intensive'],
    badgeTypes: ['ai-badge', 'new-badge'],
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=900&auto=format&fit=crop&q=80',
    imageAlt: 'AI Assisted Development',
    highlights: [
      { icon: '🤖', label: 'GitHub Copilot Mastery' },
      { icon: '💬', label: 'Prompt Engineering' },
      { icon: '🔍', label: 'AI Code Review' },
      { icon: '🛠️', label: 'OpenAI API Integration' },
      { icon: '⚡', label: 'Productivity 10x' },
      { icon: '🧪', label: 'AI Testing Tools' },
    ],
    description: 'The next-gen developer superpower — learn to harness GitHub Copilot, ChatGPT, and AI APIs to write, review, debug, and ship code 10× faster. A power-packed intensive for any developer wanting to stay ahead of the curve.',
    ctaLabel: 'Join Cohort',
    ctaVariant: 'secondary',
    syllabus: [
      {
        module: 'Module 1 — AI Development Landscape',
        topics: [
          'The AI-Augmented Developer Workflow',
          'GitHub Copilot Setup & VS Code Integration',
          'Understanding Large Language Models (LLMs)',
          'AI Tools Landscape — Copilot, Cursor, Tabnine, Codeium',
        ],
      },
      {
        module: 'Module 2 — GitHub Copilot Mastery',
        topics: [
          'Inline Completions & Ghost Text — Best Practices',
          'Copilot Chat — Context-Aware Assistance',
          'Writing Effective Comments for Better Suggestions',
          'Refactoring, Documenting & Explaining Code with AI',
          'Copilot for Tests — Unit Test Generation',
          'Copilot for CLI & Terminal Commands',
        ],
      },
      {
        module: 'Module 3 — Prompt Engineering for Developers',
        topics: [
          'Zero-Shot, Few-Shot & Chain-of-Thought Prompting',
          'Structuring Prompts for Code Generation',
          'Debugging with AI — Systematic Prompting',
          'Architecture & Design Decisions with AI',
          'Prompt Templates for Common Dev Tasks',
        ],
      },
      {
        module: 'Module 4 — OpenAI & AI API Integration',
        topics: [
          'OpenAI API — Chat, Completions, Embeddings',
          'Building an AI-Powered Feature in a Web App',
          'Function Calling & Structured Outputs',
          'Cost Management & Rate Limiting',
          'Responsible AI Use & Privacy Considerations',
        ],
      },
      {
        module: 'Module 5 — AI in DevOps & Code Quality',
        topics: [
          'AI-Assisted Code Review Workflows',
          'Automated Documentation Generation',
          'AI Security Scanning Tools',
          'GitHub Copilot for Pull Request Descriptions',
          'Capstone: Build a Full Feature Using Only AI-Assisted Code',
        ],
      },
    ],
  },
  {
    id: 'html-css-mastery',
    flagship: false,
    title: 'HTML & CSS Mastery',
    subtitle: 'HTML5 · CSS3 · Responsive Layouts · Modern UI Design',
    duration: '2 Weeks',
    price: '₹4,000',
    gst: '+ GST',
    badges: ['🚀 Fast Track', '🎨 UI Focus', '✅ Beginner Friendly'],
    badgeTypes: ['new-badge', 'placement-badge', 'ai-badge'],
    image: 'https://images.unsplash.com/photo-1523437113738-bbd3cc89fb19?w=900&auto=format&fit=crop&q=80',
    imageAlt: 'HTML and CSS Mastery Course',
    highlights: [
      { icon: '🧱', label: 'Strong HTML5 Foundation' },
      { icon: '📱', label: 'Mobile-First Responsive Design' },
      { icon: '✨', label: 'Modern UI Effects & Components' },
      { icon: '🎯', label: 'Portfolio-Ready Mini Projects' },
      { icon: '🔍', label: 'SEO + Accessibility Ready' },
      { icon: '🗂️', label: 'GitHub Deployment Guidance' },
    ],
    description: 'Perfect launchpad for beginners who want to create beautiful, responsive websites quickly. Learn job-ready frontend foundations with practical mini-projects and mentor guidance that builds confidence from day one.',
    ctaLabel: 'Start Learning',
    ctaVariant: 'secondary',
    syllabus: [
      {
        module: 'Module 1 — HTML5 Fundamentals',
        topics: [
          'HTML5 Structure & Semantic Elements',
          'Forms & Input Validation',
          'Tables, Lists & Media',
          'SEO Friendly HTML',
          'Accessibility (ARIA)',
        ],
      },
      {
        module: 'Module 2 — CSS3 Essentials',
        topics: [
          'Selectors & Specificity',
          'Box Model',
          'Colors & Typography',
          'Backgrounds & Gradients',
          'CSS Variables',
        ],
      },
      {
        module: 'Module 3 — Modern Layouts',
        topics: [
          'Flexbox Complete Guide',
          'CSS Grid',
          'Positioning',
          'Responsive Design',
          'Media Queries',
        ],
      },
      {
        module: 'Module 4 — Professional UI Design',
        topics: [
          'Animations & Transitions',
          'Hover Effects',
          'Glassmorphism',
          'Landing Pages',
          'Component Design',
        ],
      },
      {
        module: 'Module 5 — Mini Projects',
        topics: [
          'Portfolio Website',
          'Product Landing Page',
          'Responsive Business Website',
          'GitHub Deployment',
        ],
      },
    ],
  },
  {
    id: 'modern-javascript-mastery',
    flagship: false,
    title: 'Modern JavaScript Mastery',
    subtitle: 'JavaScript Core · ES6+ · Async JS · DOM · Browser APIs',
    duration: '1 Month',
    price: '₹8,000',
    gst: '+ GST',
    badges: ['⚡ High Demand', '🧠 Core Skills', '🛠️ Project Based'],
    badgeTypes: ['new-badge', 'placement-badge', 'ai-badge'],
    image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=900&auto=format&fit=crop&q=80',
    imageAlt: 'Modern JavaScript Mastery Course',
    highlights: [
      { icon: '📌', label: 'Interview-Strong JavaScript Core' },
      { icon: '🧩', label: 'Advanced Concepts Made Simple' },
      { icon: '⏳', label: 'Async JavaScript Deep Understanding' },
      { icon: '🌐', label: 'DOM + Browser API Hands-On' },
      { icon: '🧪', label: 'Debugging & Error Handling Mindset' },
      { icon: '🚀', label: 'Build 4 Real Mini Apps' },
    ],
    description: 'A complete JavaScript journey from fundamentals to advanced concepts with real project implementation. Ideal for students preparing for frontend, React, and fullstack roles.',
    ctaLabel: 'Enroll Now',
    ctaVariant: 'primary',
    syllabus: [
      {
        module: 'Module 1 — JavaScript Fundamentals',
        topics: [
          'Variables',
          'Data Types',
          'Operators',
          'Functions',
          'Scope',
          'Hoisting',
        ],
      },
      {
        module: 'Module 2 — Advanced JavaScript',
        topics: [
          'Objects',
          'Arrays',
          'Maps & Sets',
          'Closures',
          'Prototype',
          'Classes',
          'OOP',
        ],
      },
      {
        module: 'Module 3 — Modern ES6+',
        topics: [
          'Arrow Functions',
          'Destructuring',
          'Spread & Rest',
          'Modules',
          'Template Literals',
          'Optional Chaining',
        ],
      },
      {
        module: 'Module 4 — Asynchronous JavaScript',
        topics: [
          'Callbacks',
          'Promises',
          'Async/Await',
          'Fetch API',
          'Error Handling',
          'Event Loop',
        ],
      },
      {
        module: 'Module 5 — DOM & Browser APIs',
        topics: [
          'DOM Manipulation',
          'Events',
          'Local Storage',
          'Session Storage',
          'Browser APIs',
          'Forms Validation',
        ],
      },
      {
        module: 'Module 6 — Projects',
        topics: [
          'Weather App',
          'Quiz Application',
          'Expense Tracker',
          'Movie Search App',
        ],
      },
    ],
  },
  {
    id: 'reactjs-mastery',
    flagship: false,
    title: 'React.js Mastery',
    subtitle: 'React Hooks · Routing · APIs · Redux Toolkit · Performance',
    duration: '1 Month',
    price: '₹10,000',
    gst: '+ GST',
    badges: ['🔥 Trending', '⚛️ React Focused', '💼 Job Ready'],
    badgeTypes: ['flagship-badge', 'new-badge', 'placement-badge'],
    image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=900&auto=format&fit=crop&q=80',
    imageAlt: 'React.js Mastery Course',
    highlights: [
      { icon: '⚛️', label: 'React from Basics to Advanced' },
      { icon: '🪝', label: 'Hooks with Real Use Cases' },
      { icon: '🌍', label: 'Routing + API Integration' },
      { icon: '🏎️', label: 'Performance Optimization' },
      { icon: '🧠', label: 'Redux Toolkit & State Mastery' },
      { icon: '📁', label: 'Portfolio-Worthy React Projects' },
    ],
    description: 'Designed for students who want to become confident React developers quickly. Build production-style frontend applications and gain practical skills expected in modern product teams.',
    ctaLabel: 'Join React Batch',
    ctaVariant: 'primary',
    syllabus: [
      {
        module: 'Module 1 — React Fundamentals',
        topics: [
          'JSX',
          'Components',
          'Props',
          'State',
          'Events',
          'Conditional Rendering',
        ],
      },
      {
        module: 'Module 2 — React Hooks',
        topics: [
          'useState',
          'useEffect',
          'useRef',
          'useMemo',
          'useCallback',
          'Custom Hooks',
        ],
      },
      {
        module: 'Module 3 — Routing & APIs',
        topics: [
          'React Router',
          'API Integration',
          'Axios',
          'Forms',
          'Validation',
          'Authentication Basics',
        ],
      },
      {
        module: 'Module 4 — Performance',
        topics: [
          'Lazy Loading',
          'Code Splitting',
          'Memoization',
          'Error Boundaries',
          'Best Practices',
        ],
      },
      {
        module: 'Module 5 — Redux Toolkit',
        topics: [
          'Store',
          'Slice',
          'Async Thunks',
          'Global State',
          'RTK Query Basics',
        ],
      },
      {
        module: 'Module 6 — Projects',
        topics: [
          'E-Commerce Frontend',
          'Admin Dashboard',
          'Chat Application',
          'AI Powered Web App',
        ],
      },
    ],
  },
  {
    id: 'angular-mastery',
    flagship: false,
    title: 'Angular Mastery',
    subtitle: 'TypeScript · Components · RxJS · APIs · Enterprise Patterns',
    duration: '1 Month',
    price: '₹10,000',
    gst: '+ GST',
    badges: ['🏢 Enterprise Ready', '🧩 Structured Learning', '💼 Placement Aligned'],
    badgeTypes: ['new-badge', 'ai-badge', 'placement-badge'],
    image: 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=900&auto=format&fit=crop&q=80',
    imageAlt: 'Angular Mastery Course',
    highlights: [
      { icon: '🔷', label: 'TypeScript-First Frontend Skills' },
      { icon: '🧭', label: 'Routing, Guards, Lazy Loading' },
      { icon: '📝', label: 'Template + Reactive Forms' },
      { icon: '🔌', label: 'REST API & JWT Integration' },
      { icon: '📡', label: 'RxJS Observables in Practice' },
      { icon: '🏗️', label: 'Enterprise-Style Project Work' },
    ],
    description: 'A complete Angular path for learners targeting enterprise frontend roles. Learn structured architecture, scalable coding standards, and project workflows used in real companies.',
    ctaLabel: 'Build with Angular',
    ctaVariant: 'secondary',
    syllabus: [
      {
        module: 'Module 1 — Angular Fundamentals',
        topics: [
          'TypeScript',
          'Components',
          'Modules',
          'Templates',
          'Data Binding',
          'Directives',
        ],
      },
      {
        module: 'Module 2 — Services & Routing',
        topics: [
          'Dependency Injection',
          'Services',
          'Routing',
          'Lazy Loading',
          'Guards',
        ],
      },
      {
        module: 'Module 3 — Forms',
        topics: [
          'Template Forms',
          'Reactive Forms',
          'Validation',
          'Custom Validators',
        ],
      },
      {
        module: 'Module 4 — API Integration',
        topics: [
          'HttpClient',
          'REST APIs',
          'Authentication',
          'JWT',
          'Error Handling',
        ],
      },
      {
        module: 'Module 5 — Advanced Angular',
        topics: [
          'RxJS',
          'Observables',
          'Subjects',
          'Performance',
          'Build Optimization',
        ],
      },
      {
        module: 'Module 6 — Projects',
        topics: [
          'CRM Dashboard',
          'Employee Management',
          'Inventory System',
          'AI Dashboard',
        ],
      },
    ],
  },
  {
    id: 'node-express-mastery',
    flagship: false,
    title: 'Node.js & Express.js Mastery',
    subtitle: 'Backend APIs · MongoDB · Auth · Security · Deployment',
    duration: '1 Month',
    price: '₹10,000',
    gst: '+ GST',
    badges: ['🛡️ Backend Core', '🔗 API Specialist', '💼 Industry Aligned'],
    badgeTypes: ['flagship-badge', 'new-badge', 'placement-badge'],
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=900&auto=format&fit=crop&q=80',
    imageAlt: 'Node and Express Mastery Course',
    highlights: [
      { icon: '🟢', label: 'Node.js Architecture Clarity' },
      { icon: '🧰', label: 'Express API Development' },
      { icon: '🍃', label: 'MongoDB + Mongoose Mastery' },
      { icon: '🔐', label: 'JWT Auth + Authorization' },
      { icon: '🚀', label: 'Production Backend Practices' },
      { icon: '📦', label: 'Build 4 Backend Projects' },
    ],
    description: 'Become a backend-ready developer by mastering scalable API development with Node.js and Express. Learn secure coding, authentication, database design, and practical production deployment concepts.',
    ctaLabel: 'Become Backend Ready',
    ctaVariant: 'primary',
    syllabus: [
      {
        module: 'Module 1 — Node.js Fundamentals',
        topics: [
          'Node Architecture',
          'npm',
          'Modules',
          'File System',
          'Event Loop',
          'Streams',
        ],
      },
      {
        module: 'Module 2 — Express.js',
        topics: [
          'Routing',
          'Middleware',
          'REST APIs',
          'Request & Response',
          'Error Handling',
        ],
      },
      {
        module: 'Module 3 — Database',
        topics: [
          'MongoDB',
          'Mongoose',
          'CRUD',
          'Relationships',
          'Aggregation',
        ],
      },
      {
        module: 'Module 4 — Authentication',
        topics: [
          'JWT',
          'bcrypt',
          'Cookies',
          'Sessions',
          'Authorization',
        ],
      },
      {
        module: 'Module 5 — Production Backend',
        topics: [
          'File Upload',
          'Email Integration',
          'Payment Gateway Basics',
          'Security Best Practices',
          'API Documentation',
        ],
      },
      {
        module: 'Module 6 — Projects',
        topics: [
          'Authentication System',
          'Blog API',
          'E-Commerce Backend',
          'AI Backend APIs',
        ],
      },
    ],
  },
  {
    id: 'mean-mern-stack-project',
    flagship: true,
    title: 'MEAN/MERN Stack Project',
    subtitle: 'Major/Minor Projects · End-to-End Build · Documentation & Viva',
    duration: '3–4 Weeks',
    price: '₹8,000',
    gst: '+ GST',
    badges: ['🏆 Career Booster', '📚 Academic + Industry', '🎓 Major/Minor Projects'],
    badgeTypes: ['flagship-badge', 'placement-badge', 'new-badge'],
    image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=900&auto=format&fit=crop&q=80',
    imageAlt: 'MEAN and MERN stack project bootcamp',
    highlights: [
      { icon: '🧭', label: 'Project Planning to Deployment' },
      { icon: '🛠️', label: 'MEAN/MERN Setup with Mentors' },
      { icon: '🔐', label: 'Auth + CRUD + Dashboard Build' },
      { icon: '🚢', label: 'Live Deployment Practice' },
      { icon: '📄', label: 'Documentation + PPT + Viva Prep' },
      { icon: '💼', label: 'Resume-Ready Project Showcase' },
    ],
    description: 'An intensive bootcamp designed for college major/minor projects and placement portfolios. Build a complete MEAN or MERN project with mentor support, documentation, presentation prep, and final demo confidence.',
    ctaLabel: 'Book Project Slot',
    ctaVariant: 'primary',
    syllabus: [
      {
        module: 'Module 1 — Project Planning',
        topics: [
          'Project Idea Selection',
          'Requirement Analysis',
          'System Design',
          'Database Schema Design',
          'Project Folder Structure',
        ],
      },
      {
        module: 'Module 2 — MERN Stack Setup',
        topics: [
          'React Project Setup',
          'Node.js & Express Setup',
          'MongoDB Connection',
          'Environment Configuration',
          'GitHub Repository Setup',
        ],
      },
      {
        module: 'Module 3 — Project Development',
        topics: [
          'Authentication (Login/Signup)',
          'CRUD Operations',
          'API Integration',
          'File Upload',
          'Dashboard Development',
          'Responsive UI',
        ],
      },
      {
        module: 'Module 4 — Testing & Deployment',
        topics: [
          'Bug Fixing',
          'Performance Optimization',
          'Deployment (Vercel / Render / AWS Basics)',
          'GitHub Version Control',
        ],
      },
      {
        module: 'Module 5 — Documentation & Presentation',
        topics: [
          'Project Documentation',
          'ER Diagram & Flowchart',
          'PPT Preparation',
          'Viva Questions & Answers',
          'Resume Project Showcase',
          'Final Project Demonstration',
        ],
      },
    ],
  },
];

/* ─────────────── Update GitHub Copilot syllabus ─────────────── */
// Override the GitHub Copilot course with the new 14-module structure
COURSES[3] = {
  id: 'ai-copilot',
  flagship: false,
  title: 'AI-Assisted Development with GitHub Copilot',
  subtitle: 'GitHub Copilot · Prompt Engineering · AI Code Review',
  duration: '1 Month',
  price: '₹12,000',
  gst: '+ GST',
  badges: ['🤖 AI Powered', '⚡ Intensive'],
  badgeTypes: ['ai-badge', 'new-badge'],
  image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=900&auto=format&fit=crop&q=80',
  imageAlt: 'AI Assisted Development',
  highlights: [
    { icon: '🤖', label: 'GitHub Copilot Mastery' },
    { icon: '💬', label: 'Prompt Engineering' },
    { icon: '🔍', label: 'AI Code Review' },
    { icon: '⚡', label: 'Productivity 10x' },
    { icon: '🧪', label: 'AI Testing Tools' },
    { icon: '🚀', label: 'Real Projects' },
  ],
  description: 'The next-gen developer superpower — master GitHub Copilot, prompt engineering, and AI-driven development. Learn to write, review, debug, and ship code 10× faster with AI assistance.',
  ctaLabel: 'Join Cohort',
  ctaVariant: 'secondary',
  syllabus: [
    {
      module: 'Module 1 — Responsible AI with GitHub Copilot',
      topics: [
        'What is AI?',
        'AI Ethics',
        'Microsoft Responsible AI Principles',
        'Responsible use of GitHub Copilot',
      ],
    },
    {
      module: 'Module 2 — Introduction to GitHub Copilot',
      topics: [
        'What is GitHub Copilot?',
        'How GitHub Copilot works',
        'GitHub Copilot in your programming workflow',
        'Features and capabilities',
        'Benefits of using GitHub Copilot',
        'GitHub Copilot Editions',
      ],
    },
    {
      module: 'Module 3 — Introduction to Prompt Engineering with GitHub Copilot',
      topics: [
        'Prompt Engineering Fundamentals and Best Practices',
        'Writing Effective Prompts',
        'GitHub Copilot Prompt Patterns',
        'Prompt Context',
        'GitHub Copilot and Large Language Models (LLMs)',
      ],
    },
    {
      module: 'Module 4 — Introduction to Copilot Spaces',
      topics: [
        'Creating Spaces',
        'Sharing Workspaces',
        'Building Documentation and Knowledge',
        'GitHub Models with Spaces',
        'Documentation and Knowledge using Copilot Spaces',
      ],
    },
    {
      module: 'Module 5 — Using Advanced GitHub Copilot Features',
      topics: [
        'Advanced GitHub Copilot Features',
        'Use GitHub Copilot to work with Visual Studio Code',
        'GitHub Copilot Chat Best Practices',
        'Update a JavaScript profile with GitHub Copilot',
      ],
    },
    {
      module: 'Module 6 — GitHub Copilot Across IDEs, CLI, GitHub.com and Command Line',
      topics: [
        'Code Completion with GitHub Copilot',
        'GitHub Copilot Chat',
        'GitHub Copilot for Pull Requests',
        'GitHub Copilot for the Command Line',
        'GitHub Copilot in IDEs',
      ],
    },
    {
      module: 'Module 7 — Management and Customization Considerations with GitHub Copilot',
      topics: [
        'GitHub Enterprise Management',
        'Managing GitHub Copilot Users',
        'Configuring GitHub Copilot Policies',
        'Enterprise Security',
        'Privacy Considerations',
        'Telemetry & Data Collection',
      ],
    },
    {
      module: 'Module 8 — Develop Use Cases for AI with GitHub Copilot',
      topics: [
        'Identify Enterprise Productivity AI',
        'Agent-based Development',
        'AI in the Software Development Lifecycle (SDLC)',
        'Secure Software Development',
        'AI-powered Development Scenarios',
      ],
    },
    {
      module: 'Module 9 — Develop and Test with GitHub Copilot Tools',
      topics: [
        'Use Visual Studio Code Copilot for Unit Testing',
        'Generate Unit Tests with GitHub Copilot Chat',
        'Review and Improve Existing Tests',
        'Develop and Test using GitHub Copilot',
      ],
    },
    {
      module: 'Module 10 — Building Applications with GitHub Copilot Agent Mode',
      topics: [
        'What is GitHub Copilot Agent Mode?',
        'Explore Autonomous Development Assistance',
        'GitHub Skills Exercise',
      ],
    },
    {
      module: 'Module 11 — Accelerate Development with GitHub Copilot Cloud Agent',
      topics: [
        'Understanding GitHub Copilot Cloud Agent',
        'Security Roles and Permissions',
        'Assigning Tasks',
        'Invoking and Troubleshooting Cloud Agent Tasks',
        'Customizing, Extending and Validating the Copilot Cloud Agent',
        'Best Practices',
        'Expand your Team with Copilot Cloud Agent',
      ],
    },
    {
      module: 'Module 12 — Introduction to MCP Server',
      topics: [
        'Simplify your AI Workflow with GitHub MCP Server',
        'Configure, Connect and Use GitHub MCP Server in VS Code',
        'Using GitHub MCP Server with Copilot Chat',
        'Integrate MCP with GitHub Copilot',
      ],
    },
    {
      module: 'Module 13 — Leveling Up Code Reviews and Pull Requests with GitHub Copilot',
      topics: [
        'What GitHub Copilot Adds to the Review Process',
        'Using Copilot as a Reviewer on GitHub.com',
        'Learning Issues and Addressing Reviews with Copilot',
        'Managing Input and Optimizing Pull Request Reviews',
        'GitHub Copilot Code Review',
      ],
    },
    {
      module: 'Module 14 — Using GitHub Copilot with JavaScript ⭐',
      topics: [
        'What is GitHub Copilot',
        'Set up GitHub Copilot to work with Visual Studio Code',
        'Use GitHub Copilot with JavaScript',
        'Update a JavaScript portfolio with GitHub Copilot',
      ],
    },
  ],
};

/* ─────────────── Syllabus download helper ─────────────── */
const downloadSyllabus = (course) => {
  const lines = [
    `ALGODOOR — COURSE SYLLABUS`,
    `═══════════════════════════════════════`,
    ``,
    `Course: ${course.title}`,
    `Duration: ${course.duration}`,
    `Fee: ${course.price} ${course.gst}`,
    `Technologies: ${course.subtitle}`,
    ``,
    `─────────────────────────────────────────`,
    `KEY HIGHLIGHTS`,
    `─────────────────────────────────────────`,
    ...course.highlights.map((h) => `  ${h.icon}  ${h.label}`),
    ``,
    `─────────────────────────────────────────`,
    `COURSE SYLLABUS`,
    `─────────────────────────────────────────`,
    ``,
    ...course.syllabus.flatMap((m) => [
      `▶  ${m.module}`,
      ...m.topics.map((t) => `     ▸  ${t}`),
      ``,
    ]),
    `─────────────────────────────────────────`,
    ``,
    `For admissions & queries:`,
    `  🌐  www.algodoor.com`,
    `  📞  +91-7489017551`,
    ``,
    `© Algodoor — Bridging Academia & Industry`,
  ];

  const text = lines.join('\n');
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Algodoor_${course.id}_syllabus.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/* ─────────────── CourseCard component ─────────────── */
const CourseCard = ({ course, navigate, isTargeted }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const panelRef = useRef(null);

  const handleCopyCourseLink = async () => {
    const courseHash = `course-${course.id}`;
    const directLink = `${window.location.origin}/courses#${courseHash}`;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(directLink);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = directLink;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch (_err) {
      setCopied(false);
    }
  };

  const handleEnroll = () => {
    // Navigate to home page with course enrollment parameters
    navigate(`/?course=${course.id}&fromEnroll=true#enroll-form`);
    // Scroll to query content section after a brief delay to ensure navigation completes
    setTimeout(() => {
      const el = document.getElementById('enroll-form');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div id={`course-${course.id}`} className={`course-card${course.flagship ? ' flagship' : ''}${isTargeted ? ' is-targeted' : ''}`}>
      {/* Image area */}
      <div className="course-card-image-wrap">
        <img
          src={course.image}
          alt={course.imageAlt}
          loading="lazy"
          onError={(e) => {
            e.target.src = `https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=900&auto=format&fit=crop&q=80`;
          }}
        />
        <div className="course-card-image-overlay" />
        <div className="course-card-badges">
          {course.badges.map((badge, i) => (
            <span key={badge} className={`course-badge ${course.badgeTypes[i] || 'new-badge'}`}>
              {badge}
            </span>
          ))}
        </div>
        <div className="course-card-price-tag">
          <div className="course-price-amount">{course.price}</div>
          <div className="course-price-note">{course.gst}</div>
          {course.flagship && (
            <div className="course-scarcity-text">Only 15 Spots Left!</div>
          )}
        </div>
      </div>

      {/* Card body */}
      <div className="course-card-body">
        <div className="course-card-meta">
          <span className="course-meta-item">⏱ {course.duration}</span>
          <span className="course-meta-item" style={{ color: '#AE67FA' }}>
            ✦ {course.flagship ? 'Flagship Program' : 'Specialisation Program'}
          </span>
          <button
            type="button"
            className={`course-link-copy-btn${copied ? ' copied' : ''}`}
            onClick={handleCopyCourseLink}
            aria-label={`Copy direct link for ${course.title}`}
            title={copied ? 'Link copied' : 'Copy direct link'}
          >
            <FiLink />
          </button>
        </div>

        <h3 className="course-card-title">{course.title}</h3>
        <p className="course-card-subtitle">{course.description}</p>

        <div className="course-highlights-row">
          {course.highlights.map((h) => (
            <span key={h.label} className="course-highlight-tag">
              <span className="tag-icon">{h.icon}</span>
              {h.label}
            </span>
          ))}
        </div>

        {/* Accordion toggle */}
        <button
          type="button"
          className={`course-accordion-btn${open ? ' open' : ''}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span>📋 View Full Syllabus ({course.syllabus.length} Modules)</span>
          <span className={`course-accordion-chevron${open ? ' open' : ''}`}>▼</span>
        </button>

        {/* Accordion panel */}
        <div className={`course-accordion-panel${open ? ' open' : ''}`} ref={panelRef}>
          <div className="course-accordion-inner">
            {course.syllabus.map((m) => (
              <div key={m.module} className="course-module">
                <div className="course-module-title">{m.module}</div>
                <ul className="course-module-topics">
                  {m.topics.map((t) => <li key={t}>{t}</li>)}
                </ul>
              </div>
            ))}
            <div className="course-download-row">
              <button
                type="button"
                className="course-download-btn"
                onClick={() => downloadSyllabus(course)}
              >
                ⬇ Download Syllabus
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Card footer */}
      <div className="course-card-footer">
        <button
          type="button"
          className={`course-enroll-btn ${course.ctaVariant}`}
          onClick={handleEnroll}
        >
          {course.ctaVariant === 'primary' ? '🚀' : '📩'} {course.ctaLabel} — {course.price}
        </button>
      </div>
    </div>
  );
};

/* ─────────────── Main CoursesPage ─────────────── */
const CoursesPage = ({ focusRoadmaps = false }) => {
  const navigate = useNavigate();
  const roadmapSectionRef = useRef(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [targetedCourseHash, setTargetedCourseHash] = useState('');

  const handleCopyRoadmapShareLink = async () => {
    const shareUrl = 'https://www.algodoor.com/courses-roadmaps';

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 1800);
    } catch (_err) {
      setShareCopied(false);
    }
  };

  useEffect(() => {
    if (!focusRoadmaps) return;

    const timer = window.setTimeout(() => {
      roadmapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 180);

    return () => window.clearTimeout(timer);
  }, [focusRoadmaps]);

  useEffect(() => {
    const scrollToHashCard = () => {
      const hash = window.location.hash.replace('#', '');
      if (!hash || !hash.startsWith('course-')) {
        setTargetedCourseHash('');
        return;
      }

      const el = document.getElementById(hash);
      if (!el) return;

      setTargetedCourseHash(hash);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => setTargetedCourseHash(''), 2600);
    };

    const timer = window.setTimeout(scrollToHashCard, 180);
    window.addEventListener('hashchange', scrollToHashCard);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('hashchange', scrollToHashCard);
    };
  }, []);

  const HIGHLIGHTS_STRIP = [
    'Industry Live Projects',
    'AI-Assisted Programming',
    'Placement Support',
    'Mock Interviews',
    'ATS Resume Building',
    'Expert Mentorship',
    'Git & GitHub Workflow',
    'Cloud Deployment',
    'Docker & CI/CD',
    'Real Codebase Experience',
  ];

  const ROADMAP_LINKS = [
    {
      id: 'js-roadmap',
      icon: '🟨',
      title: 'Roadmap to Fullstack JavaScript Course',
      subtitle: 'React, Node.js, MongoDB, interview prep, and placement strategy in one visual flow.',
      path: '/roadmap-to-fullstack-javascript-course',
    },
    {
      id: 'java-roadmap',
      icon: '☕',
      title: 'Roadmap to Fullstack Java Course',
      subtitle: 'Core Java, Spring Boot, SQL, project track, and job-focused preparation plan.',
      path: '/roadmap-to-fullstack-java-course',
    },
    {
      id: 'aws-devops-roadmap',
      icon: '☁️',
      title: 'Roadmap to AWS DevOps Course',
      subtitle: 'Cloud fundamentals, Linux, networking, AWS services, CI/CD, Docker, and monitoring.',
      path: '/roadmap-to-aws-devops-course',
    },
  ];

  const TECH_PILLS = [
    { emoji: '⚛️', label: 'React.js' },
    { emoji: '▲', label: 'Next.js' },
    { emoji: '🟢', label: 'Node.js' },
    { emoji: '🍃', label: 'MongoDB' },
    { emoji: '🟨', label: 'JavaScript' },
    { emoji: '🔴', label: 'Redux Toolkit' },
    { emoji: '☁️', label: 'AWS' },
    { emoji: '🐳', label: 'Docker' },
    { emoji: '⚙️', label: 'CI/CD' },
    { emoji: '🐧', label: 'Linux' },
    { emoji: '🤖', label: 'GitHub Copilot' },
    { emoji: '🧠', label: 'OpenAI API' },
    { emoji: '🔒', label: 'REST APIs' },
    { emoji: '🔀', label: 'Git & GitHub' },
    { emoji: '🌐', label: 'HTML5 / CSS3' },
    { emoji: '📦', label: 'Express.js' },
    { emoji: '🔷', label: 'TypeScript' },
    { emoji: '🎨', label: 'Tailwind CSS' },
  ];

  const DIFFERENTIATORS = [
    {
      icon: '📡',
      title: 'Continuous Expert-Led Sessions',
      description: 'We continuously run sessions with students and experts from leading companies, so learners gain practical exposure to trending technologies and modern IT work culture.',
    },
    {
      icon: '🏆',
      title: 'Cross-College Hackathon Experience',
      description: 'We organize hackathons with students from other colleges and universities, helping our students benchmark their level and collaborate with strong technical peers.',
    },
    {
      icon: '🎤',
      title: 'High-Impact Mock Interviews',
      description: 'We conduct mock interviews with experts from top companies, helping students develop stronger technical depth, communication, and high-level problem-solving ability.',
    },
    {
      icon: '💼',
      title: 'Live Industry Project Work',
      description: 'We provide live industry project work so students gain hands-on experience with practical development workflows, real delivery expectations, and production-style problem solving.',
    },
  ];

  const YOUTUBE_VIDEOS = [
    {
      id: 'qMRT3TjlsTA',
      title: 'Algodoor YouTube Video 1',
      src: 'https://www.youtube.com/embed/qMRT3TjlsTA?si=XqsiGNvSIwIb29_p',
    },
    {
      id: 'Anz0G9G2m9w',
      title: 'Algodoor YouTube Video 2',
      src: 'https://www.youtube.com/embed/Anz0G9G2m9w?si=Om50USXi2v29Cz8V',
    },
    {
      id: 'TxpAF4Z1EOQ',
      title: 'Algodoor YouTube Video 3',
      src: 'https://www.youtube.com/embed/TxpAF4Z1EOQ?si=sD1n6UoLPYR25YrR',
    },
    {
      id: '9BT-VXVf21Y',
      title: 'Algodoor YouTube Video 4',
      src: 'https://www.youtube.com/embed/9BT-VXVf21Y?si=x0ev61tLzXQt8reV',
    },
  ];

  const breadcrumbItems = focusRoadmaps
    ? [
      { label: 'Home', to: '/' },
      { label: 'Courses', to: '/courses' },
      { label: 'Roadmaps' },
    ]
    : [
      { label: 'Home', to: '/' },
      { label: 'Courses' },
    ];

  return (
    <div className="courses-page">
      <SEOMeta
        title={focusRoadmaps
          ? 'Roadmaps for JavaScript, Java, and AWS DevOps | Algodoor'
          : 'Best Software Training in Bhopal | Courses | Algodoor'}
        description={focusRoadmaps
          ? 'Explore Algodoor roadmap hub for Fullstack JavaScript, Fullstack Java guidance, and AWS DevOps career paths with placement-oriented milestones.'
          : 'Placement-focused software training in Bhopal with practical learning, expert mentors, mock interviews, and industry-ready courses.'}
        url={focusRoadmaps ? '/courses-roadmaps' : '/courses'}
        socialTitle={focusRoadmaps ? 'Explore Career Roadmaps | JavaScript, Java & AWS DevOps' : null}
        socialDescription={focusRoadmaps ? 'Shareable roadmap hub for students: step-by-step guidance for Fullstack JavaScript and AWS DevOps, plus Java fullstack direction.' : null}
        socialImage={focusRoadmaps ? '/main-image.png' : null}
        type="WebPage"
        schemaMarkup={{
          '@context': 'https://schema.org',
          '@graph': [
            generateOrganizationSchema(),
            generateLocalBusinessSchema(),
            generateFAQSchema(),
          ],
        }}
      />

      <div className="gradient__bg">
        <Navbar />
      </div>

      <Breadcrumbs items={breadcrumbItems} />

      {/* ── Hero ── */}
      <section className="courses-hero">
        <div className="courses-hero-badge">🚀 Industry-Oriented Training Programs</div>
        <h1>
          Bridge the Gap Between<br />
          <span>Academia & Industry</span>
        </h1>
        <p>
          Practical, placement-focused programs designed with real software development workflows,
          AI-assisted programming, live industry projects, and expert mentorship from top IT companies.
        </p>

        <div className="courses-hero-stats">
          {[
            { num: String(COURSES.length), label: 'Programs Offered' },
            { num: '100%', label: 'Practical Learning' },
            { num: '10+', label: 'Technologies Covered' },
            { num: '1-3', label: 'Months Duration' },
          ].map((s) => (
            <div key={s.label} className="courses-hero-stat">
              <div className="courses-hero-stat-num">{s.num}</div>
              <div className="courses-hero-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Highlights strip ── */}
      <div className="courses-highlights-strip">
        <div className="courses-highlights-inner">
          {HIGHLIGHTS_STRIP.map((h) => (
            <span key={h} className="courses-highlight-chip">
              <span className="chip-dot" />
              {h}
            </span>
          ))}
        </div>
      </div>

      <section id="courses-roadmap-access" ref={roadmapSectionRef} className="courses-roadmap-access">
        <div className="courses-section-label" style={{ justifyContent: 'center', display: 'flex' }}>🧭 Guided Learning Paths</div>
        <h2 className="courses-section-title" style={{ textAlign: 'center' }}>
          Explore Detailed <span className="gradient__text">Roadmaps</span>
        </h2>
        <p className="courses-section-sub" style={{ textAlign: 'center', margin: '0 auto 1.8rem' }}>
          Want a clear week-by-week plan before enrollment? Open these roadmap pages to see the learning sequence,
          interview focus, job opportunities, and practical milestones.
        </p>

        <div className="courses-roadmap-share-row">
          <button
            type="button"
            className="courses-copy-share-btn"
            onClick={handleCopyRoadmapShareLink}
          >
            {shareCopied ? 'Link Copied' : 'Copy Share Link'}
          </button>
        </div>

        <div className="courses-roadmap-grid">
          {ROADMAP_LINKS.map((roadmap) => (
            <article key={roadmap.id} className="courses-roadmap-card">
              <div className="roadmap-card-icon" aria-hidden="true">{roadmap.icon}</div>
              <h3>{roadmap.title}</h3>
              <p>{roadmap.subtitle}</p>
              <button
                type="button"
                className="courses-roadmap-btn"
                onClick={() => navigate(roadmap.path)}
              >
                Open Roadmap →
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* ── Flagship courses ── */}
      <section className="courses-section">
        <CountdownTimer 
          targetDate="2026-07-15" 
          batchStartDate="6th August 2026" 
        />
        <div className="courses-section-label">⭐ Flagship Programs</div>
        <h2 className="courses-section-title">
          Our <span className="gradient__text">USP Programs</span> —{' '}
          Built for Placement &amp; Industry
        </h2>
        <p className="courses-section-sub">
          These two programs are designed end-to-end with a focus on actual industry workflows,
          placement readiness, and AI-powered modern development.
        </p>

        <div className="courses-grid">
          {COURSES.filter((c) => c.flagship).map((c) => (
            <CourseCard
              key={c.id}
              course={c}
              navigate={navigate}
              isTargeted={targetedCourseHash === `course-${c.id}`}
            />
          ))}
        </div>
      </section>

      {/* ── Placement banner ── */}
      <div className="courses-placement-banner">
        <div className="placement-banner-content">
          <h2>
            Your Career Launch<br />
            <span className="gradient__text">Starts Here</span>
          </h2>
          <p>
            We don't just teach code — we prepare you for the real software industry.
            From ATS-optimised resumes and mock technical interviews to live project work
            and expert sessions from engineers at leading companies, everything is designed
            around your placement success.
          </p>
          <button
            type="button"
            className="placement-banner-cta"
            onClick={() => {
              navigate('/?fromEnroll=true#enroll-form');
              setTimeout(() => {
                const el = document.getElementById('enroll-form');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
          >
            📩 Talk to an Advisor
          </button>
        </div>
        <div className="placement-banner-features">
          {[
            {
              icon: '🎯',
              title: 'Placement Support Assistance',
              desc: 'Dedicated placement prep — job portals, referrals, and HR assistance throughout your journey.',
            },
            {
              icon: '🗣️',
              title: 'Mock Interviews & Expert Interactions',
              desc: 'Simulated technical interviews and Q&A sessions with engineers from top leading IT companies.',
            },
            {
              icon: '💼',
              title: 'Industry Live Project Work',
              desc: 'Work on real-world projects that mirror actual software development team workflows.',
            },
            {
              icon: '📄',
              title: 'ATS-Friendly Resume Building',
              desc: 'Craft a resume that passes ATS filters and stands out to hiring managers.',
            },
          ].map((f) => (
            <div key={f.title} className="placement-feature-item">
              <div className="placement-feature-icon">{f.icon}</div>
              <div className="placement-feature-text">
                <h4>{f.title}</h4>
                <p>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Specialisation courses ── */}
      <section className="courses-section" style={{ paddingTop: 0 }}>
        <div className="courses-section-label">🎓 Specialisation Programs</div>
        <h2 className="courses-section-title">
          Focused <span className="gradient__text">Skill Programs</span>
        </h2>
        <p className="courses-section-sub">
          Targeted courses for specific skill sets — perfect for students looking to specialise
          or professionals wanting to upskill fast.
        </p>

        <div className="courses-grid">
          {COURSES.filter((c) => !c.flagship).map((c) => (
            <CourseCard
              key={c.id}
              course={c}
              navigate={navigate}
              isTargeted={targetedCourseHash === `course-${c.id}`}
            />
          ))}
        </div>
      </section>

      {/* ── Technologies cloud ── */}
      <section className="courses-tech-section">
        <div className="courses-section-label" style={{ justifyContent: 'center', display: 'flex' }}>🛠 Technologies We Cover</div>
        <h2 className="courses-section-title" style={{ textAlign: 'center' }}>
          Industry-Relevant <span className="gradient__text">Tech Stack</span>
        </h2>
        <div className="courses-tech-cloud">
          {TECH_PILLS.map((t) => (
            <span key={t.label} className="tech-pill">
              {t.emoji} {t.label}
            </span>
          ))}
        </div>
      </section>

      <section className="courses-differentiator-section">
        <div className="courses-section-label" style={{ justifyContent: 'center', display: 'flex' }}>⚡ Why We Are Different</div>
        <h2 className="courses-section-title" style={{ textAlign: 'center' }}>
          How Algodoor Stands <span className="gradient__text">Apart from Others</span>
        </h2>
        <div className="courses-differentiator-grid">
          {DIFFERENTIATORS.map((item) => (
            <article key={item.title} className="courses-differentiator-card">
              <span className="diff-icon" aria-hidden="true">{item.icon}</span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="courses-youtube-section">
        <div className="courses-section-label" style={{ justifyContent: 'center', display: 'flex' }}>▶ Social Learning</div>
        <h2 className="courses-section-title" style={{ textAlign: 'center' }}>
          From Our <span className="gradient__text">YouTube Community</span>
        </h2>
        <p className="courses-youtube-sub">
          Explore sessions, practical explanations, and student-focused learning videos from our social channels.
        </p>

        <div className="courses-youtube-grid">
          {YOUTUBE_VIDEOS.map((video) => (
            <article key={video.id} className="courses-youtube-card">
              <div className="courses-youtube-frame-wrap">
                <iframe
                  src={video.src}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default CoursesPage;
